

















































































































































/* General */
import Vue from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import SortAssistant from '@/helpers/sort-assistant'

/* Data types */
import {
  IItemForSelection,
  IPersonBase,
  IPriorityLevelForSelection,
  IStatusForSelection,
} from '@/api/types/response.types'
import { SortOptionValues } from '@/types'
import { IItemFiltersPanelState } from '@/types/data-objects/listing'
import { EEntities } from '@/types'

/* API */

export default Vue.extend({
  name: 'ItemFiltersPanel',

  props: {
    isKanban: {
      type: Boolean,
      default: false,
    },
    /* Kanban only */
    showUnsortedTasksOnTopForKanban: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isDefaultItemTraitSet: {
      type: Boolean,
      default: false,
    },
    isAllTasks: {
      type: Boolean,
      default: false,
    },
    isAllConvos: {
      type: Boolean,
      default: false,
    },
    /* Needed to pull creators in ifp-creator (otherwise pulls from entire account) */
    singleProjectId: {
      type: Number,
      default: null,
    },
    /* Needed to pull customers in ifp-customer (otherwise pulls from entire account) */
    singleMailboxId: {
      type: Number,
      default: null,
    },
    /* Item trait set props */
    searchTerm: {
      type: String,
      required: true,
    },
    selectedEntityIds: {
      type: Array as () => number[],
      default: null,
    },
    selectedAssigneeIds: {
      type: Array as () => number[],
      required: true,
    },
    assignedNotToYou: {
      type: Boolean,
      default: false,
    },
    selectedJobAppointeeGroupingsString: {
      type: String,
      default: '',
    },
    selectedCreatorIds: {
      type: Array as () => number[],
      default: null,
    },
    selectedCustomerIds: {
      type: Array as () => number[],
      default: null,
    },
    selectedStatusIds: {
      type: Array as () => number[],
      required: true,
    },
    statusType: {
      type: String,
      default: '',
    },
    selectedPriorityIds: {
      type: Array as () => number[],
      default: null,
    },
    selectedDueType: {
      type: Number,
      default: null,
    },
    selectedIncludingTagIds: {
      type: Array as () => number[],
      required: true,
    },
    selectedExcludingTagIds: {
      type: Array as () => number[],
      required: true,
    },
    createdWithinDays: {
      type: Number,
      default: undefined,
    },
    lastUpdatedWithinDays: {
      type: Number,
      default: undefined,
    },
    startDueDate: {
      type: String,
      default: '',
    },
    endDueDate: {
      type: String,
      default: '',
    },
    selectedSort: {
      type: String,
      default: SortOptionValues.UPDATED_RECENT,
      // required: true,
    },
    onlyWatching: {
      type: Boolean,
      default: false,
    },
    /* Item trait set props: Convos only */
    isSpam: {
      type: Boolean,
      default: false,
    },
    /* Options for dropdowns */
    entityOptions: {
      type: Array as () => IItemForSelection[],
      default: null,
    },
    assigneeOptions: {
      type: Array as () => IPersonBase[],
      required: true,
    },
    jobOptions: {
      type: Array as () => IItemForSelection[],
      required: true,
    },
    priorityOptions: {
      type: Array as () => IPriorityLevelForSelection[],
      required: true,
    },
    activeStatusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },
    inactiveStatusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },
    tagOptions: {
      type: Array as () => IItemForSelection[],
      required: true,
    },
    dueDateOptions: {
      type: Array as () => IItemForSelection[],
      required: true,
    },
  },

  data() {
    return {
      SortAssistant,
      state: {
        showFilterSpam: false,
        showFilterWatching: false,
        showFilterCreatedWithinDays: false,
        showFilterLastUpdatedWithinDays: false,
        showFilterSort: !this.isKanban,
      } as IItemFiltersPanelState,
    }
  },

  computed: {
    isAll(): boolean {
      return this.isAllTasks || this.isAllConvos
    },
    allEntityType(): string {
      if (this.isAllTasks) {
        return EEntities.PROJECTS
      } else if (this.isAllConvos) {
        return EEntities.MAILBOXES
      }
      return ''
    },
    isSingleMailbox(): boolean {
      return this.singleMailboxId !== null
    },
    isSingleProject(): boolean {
      return this.singleProjectId !== null
    },
    hasCreatedWithinDays(): boolean {
      return this.createdWithinDays !== null && this.createdWithinDays !== 0
    },
    hasLastUpdatedWithinDays(): boolean {
      return (
        this.lastUpdatedWithinDays !== null && this.lastUpdatedWithinDays !== 0
      )
    },
    hasTags(): boolean {
      return (
        this.selectedIncludingTagIds.length > 0 ||
        this.selectedExcludingTagIds.length > 0
      )
    },
    hasJobs(): boolean {
      return this.jobOptions.length > 0
    },
  },

  /* Toggling filter visibility - Read me: 

    Some <ifp-filter-xxx /> components (ifp-filters) are always visible:

      * Assignees
      * Creators
      * Customers
      * Status
      * Priority
      * Due
      * Tags

    ---
    
    Some ifp-filters "show themselves" based on their current respective prop values
    (e.g. <ifp-filter-assignee /> when selectedAssigneeIDs.length > 0). Hence, all ifp-filters emit the update to their
    value to this component, then this component syncs the data back to the parent, which then updates the prop and 
    triggers the watchers below to do the show/hiding by updating its corresponding data.state attribute).

    The <ifp-picker /> also allows a user to expose a hidden ifp-filter manually so the user can start selecting values. The
    corresponding data.state attribute is updated *directly*.  However, if the user ends up reverting a value back to the default
    (i.e. usually "nothing"), then the filter will naturally go away via the watcher. (e.g. User exposes <ifp-filter-assignee /> 
    from the <ifp-picker />, then (1) selects an assignee, then (2) deselects it. The 2nd update will trigger the watcher to 
    hide the ifp-filter.

    Note: These watchers have immediate: true so that the ifp-filter can be exposed on creation if they already have values
    (via the QS, local store, etc.)

      * Created within days
      * Last updated within days
      * Title / ref #
      * You're watching
      * Spam

    The user can also manually close ifp-filters via the "X" button. In most cases, this simply emits the default value
    back to this component, and the normal propogation mentioned above happens.

    In summary, these ifp-filter components:

      a) Show/hide themselves based on the watcher of their corresponding value.
      b) Can manually show from the <ifp-picker /> user action.
      c) Can manually hide from the ifp-filter "X" button (by emitting the default value to this component.)

    ---

    Additionally, in some cases, we want the manual showing of a ifp-filter via <ifp-picker /> to start with a custom value other than
    the default (unless a non-default value already exists), so we can watch on the data.state attribute for when the value becomes
    true (DO NOT SET TO 'immediate: true' so this only fires on a <ifp-picker /> manual update). This is OK because it will never become
    'false' again unless c) happens which sets the value back to the default. Then, showing the filter again sets it back to the 
    custom value.

      1) Created within days --> 1
      2) Last updated within days  -> 1
      3) Spam --> true
      4) Only Watching --> true

    -KC 6/24/22
  */

  watch: {
    createdWithinDays: {
      immediate: true,
      handler(val) {
        this.state.showFilterCreatedWithinDays = this.hasCreatedWithinDays
      },
    },
    lastUpdatedWithinDays: {
      immediate: true,
      handler(val) {
        this.state.showFilterLastUpdatedWithinDays = this.hasLastUpdatedWithinDays
      },
    },
    onlyWatching: {
      immediate: true,
      handler(val) {
        this.state.showFilterWatching = val
      },
    },
    /* Item trait set props: Convos only */
    isSpam: {
      immediate: true,
      handler(val) {
        this.state.showFilterSpam = val
      },
    },
    'state.showFilterCreatedWithinDays': {
      handler(show) {
        if (show && !this.hasCreatedWithinDays) {
          this.$emit('update:createdWithinDays', 1)
        }
      },
    },
    'state.showFilterLastUpdatedWithinDays': {
      handler(show) {
        if (show && !this.hasLastUpdatedWithinDays) {
          this.$emit('update:lastUpdatedWithinDays', 1)
        }
      },
    },
    'state.showFilterSpam': {
      handler(show) {
        if (show) {
          this.$emit('update:isSpam', true)
        }
      },
    },
    'state.showFilterWatching': {
      handler(show) {
        if (show) {
          this.$emit('update:onlyWatching', true)
        }
      },
    },
  },

  methods: {
    handleFilterReset() {
      this.$emit('reset-filters')
    },
  },
})
